import React, { useState, useContext, useCallback, useEffect, useMemo } from 'react';

import CheckoutModal from 'ggApp/modules/newCheckout/components/CheckoutModal';
import { AuthModal } from 'ggApp/modules/auth/v3/components';
import { COMMON_QUERY_PARAM, MODAL_QUERY_ACTIVE } from 'ggApp/modules/router/constants';
import { getOrderFromCookiesBasedStore } from 'ggApp/shared/context/Order/cookies';
import { OrderModes } from 'ggApp/shared/context/Order/types';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import useMount from 'ggApp/shared/hooks/useMount';
import PreventSelfCheckoutModal from 'ggApp/shared/modals/preventSelfCheckout';

const SinglePageCheckout = ({
    checkoutFlow,
    isLoggedIn,
    isIncompletePrivateAccount,
    isIncompleteBusinessAccount,
    redirect,
    locationQuery,
    b2bRedirection,
    initialAuthFlow,
    checkStoreAndUserType,
    isBlockManagedAccountCheckout,
    isLegacyBusinessAccount,
}) => {
    const {
        cart: { items },
        activeStore,
        cartLoaded,
        orderMode,
    } = useContext(OrderContext);
    const [preventSelfCheckoutModal, setPreventSelfCheckoutModal] = useState(null);
    const [isFirstCheckoutOpening, setIsFirstCheckoutOpening] = useState(true);
    const [checkoutModal, setCheckoutModal] = useState(null);
    const [authModal, setAuthModal] = useState(null);
    const [authModalFlow, setAuthModalFlow] = useState(initialAuthFlow);

    const createModalRefCheckout = useCallback((modal) => {
        setCheckoutModal(modal);
    }, []);

    const createModalRefAuth = useCallback((modal) => {
        setAuthModal(modal);
    }, []);

    const createModalRefPreventCheckout = useCallback((modal) => {
        setPreventSelfCheckoutModal(modal);
    }, []);

    const openAuthModal = useCallback(() => {
        if (authModal) {
            authModal.openModal();
        }
    }, [authModal]);

    const openPreventSelfCheckoutModal = useCallback(() => {
        if (preventSelfCheckoutModal) {
            preventSelfCheckoutModal.openModal();
        }
    }, [preventSelfCheckoutModal]);

    const openCheckoutModal = useCallback(() => {
        const userAuthorized =
            isLoggedIn &&
            !isIncompleteBusinessAccount &&
            !isIncompletePrivateAccount &&
            !isLegacyBusinessAccount;

        if (!userAuthorized) {
            openAuthModal();
            return;
        }

        if (isBlockManagedAccountCheckout) {
            openPreventSelfCheckoutModal();
            return;
        }

        if (checkoutModal) {
            checkoutModal.openModal();
        }
    }, [
        checkoutModal,
        isIncompletePrivateAccount,
        isIncompleteBusinessAccount,
        isLegacyBusinessAccount,
        isBlockManagedAccountCheckout,
        isLoggedIn,
        openAuthModal,
        openPreventSelfCheckoutModal,
    ]);

    const isCartNotCompleted = useMemo(
        () => cartLoaded && (!items?.length || (orderMode === OrderModes.Mix && items.length < 3)),
        [cartLoaded, items, orderMode],
    );

    const redirectToHome = useCallback(() => {
        redirect(`/${activeStore.code}-${activeStore.activeLanguage}/`, true);
    }, [activeStore.activeLanguage, activeStore.code, redirect]);

    const handleAuthSuccess = useCallback(() => {
        if (isIncompletePrivateAccount) {
            setAuthModalFlow(initialAuthFlow);
        }
        if (isCartNotCompleted) {
            redirectToHome();
            return;
        }
        openCheckoutModal();
    }, [
        isIncompletePrivateAccount,
        openCheckoutModal,
        isCartNotCompleted,
        redirectToHome,
        initialAuthFlow,
    ]);

    const onCloseAuthModal = useCallback(
        (payload) => {
            if (!payload.autoClose) {
                redirectToHome();
            }
        },
        [redirectToHome],
    );

    const shouldBeRedirected = useMemo(() => {
        const wrongStore = b2bRedirection?.shouldBeRedirectedToBusiness;
        const isCheckoutFlow =
            locationQuery[COMMON_QUERY_PARAM.waitingModal] !== MODAL_QUERY_ACTIVE;
        const hasOrder = getOrderFromCookiesBasedStore(activeStore.code);

        return (isLoggedIn && (wrongStore || (isCartNotCompleted && isCheckoutFlow))) || !hasOrder;
    }, [b2bRedirection, locationQuery, activeStore.code, isLoggedIn, isCartNotCompleted]);

    useMount(checkStoreAndUserType);

    useEffect(() => {
        if (shouldBeRedirected) {
            redirectToHome();
            return;
        }

        if (!cartLoaded || !checkoutModal || !isFirstCheckoutOpening || !preventSelfCheckoutModal) {
            return;
        }

        openCheckoutModal();
        setIsFirstCheckoutOpening(false);
    }, [
        preventSelfCheckoutModal,
        cartLoaded,
        checkoutModal,
        isFirstCheckoutOpening,
        openCheckoutModal,
        redirectToHome,
        shouldBeRedirected,
    ]);
    return (
        <div data-testid="page-checkout">
            <CheckoutModal
                flow={checkoutFlow}
                modalRef={createModalRefCheckout}
                onClose={redirectToHome}
                withBackgroundColor={false}
            />
            <PreventSelfCheckoutModal
                modalRef={createModalRefPreventCheckout}
                handleClose={redirectToHome}
            />
            <AuthModal
                authModalFlow={authModalFlow}
                modalRef={createModalRefAuth}
                flow={authModalFlow}
                onSuccess={handleAuthSuccess}
                onClose={onCloseAuthModal}
                withBackgroundColor={false}
                options={{ hasSuccessSignupStep: false, isCheckout: true }}
            />
        </div>
    );
};

export default SinglePageCheckout;
